import { Button } from "@/components/ui/button";
import { useState, useEffect, memo, useCallback } from "react";
import { Link } from "react-router-dom";

/**
 * Optimized Hero component with performance improvements:
 * - Memoized to prevent unnecessary re-renders
 * - Optimized typing animation with single timer
 * - Reduced DOM manipulations
 * - CSS-based animations where possible
 */
const OptimizedHero = memo(() => {
  const [typedText, setTypedText] = useState("");
  const [showCursor, setShowCursor] = useState(true);
  const fullText = "Build. Train. Own. Forever.";

  // Optimize typing animation with single effect
  useEffect(() => {
    let i = 0;
    let typingTimer: NodeJS.Timeout;
    let cursorTimer: NodeJS.Timeout;

    const typeText = () => {
      if (i < fullText.length) {
        setTypedText(fullText.slice(0, i + 1));
        i++;
        typingTimer = setTimeout(typeText, 80);
      }
    };

    const blinkCursor = () => {
      setShowCursor(prev => !prev);
      cursorTimer = setTimeout(blinkCursor, 500);
    };

    typeText();
    blinkCursor();

    return () => {
      clearTimeout(typingTimer);
      clearTimeout(cursorTimer);
    };
  }, [fullText]);

  // Memoized scroll handler
  const handleScrollToHowItWorks = useCallback(() => {
    const element = document.querySelector('#how-it-works');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden section-spacing">
      {/* Background with CSS-only animations */}
      <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/10" />
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-float-enhanced" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/15 rounded-full blur-3xl animate-float-enhanced" style={{ animationDelay: '-2s' }} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-secondary/10 rounded-full blur-3xl animate-float-enhanced" style={{ animationDelay: '-4s' }} />
      </div>
      
      {/* Optimized particles with CSS */}
      <div className="particles">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              animationDelay: `${Math.random() * 8}s`,
              animationDuration: `${6 + Math.random() * 4}s`
            }}
          />
        ))}
      </div>

      <div className="container relative z-10 px-4 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          {/* Enhanced Headline */}
          <div className="mb-8 lg:mb-12">
            <h1 className="text-5xl sm:text-6xl lg:text-8xl font-display font-black mb-4 lg:mb-6 animate-slide-up">
              <span className="block text-foreground">Stop Renting Content Teams—</span>
              <span className="block bg-gradient-brand bg-clip-text text-transparent">
                {typedText}
                <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} transition-opacity duration-100`}>|</span>
              </span>
            </h1>
            
            {/* Power Statement */}
            <div className="glass-panel p-4 lg:p-6 mb-6 lg:mb-8 max-w-2xl mx-auto animate-bounce-in" style={{animationDelay: '0.5s'}}>
              <p className="text-lg lg:text-xl font-bold text-primary">
                🚀 From Agency Dependency to Content Ownership in 90 Days
              </p>
            </div>
          </div>

          {/* Enhanced Subheadline */}
          <p className="text-xl sm:text-2xl lg:text-3xl text-muted-foreground mb-10 lg:mb-16 max-w-4xl mx-auto leading-relaxed px-4 sm:px-0 animate-slide-up">
            We don't just deliver content—we build, train, and transfer a complete in-house content machine that compounds results 
            <span className="text-primary font-semibold"> while you sleep</span>, requiring only 60–90 minutes/week from you.
          </p>

          {/* Enhanced CTAs */}
          <div className="flex flex-col sm:flex-row gap-6 lg:gap-8 justify-center mb-12 lg:mb-16 px-4 sm:px-0">
            <Link to="/contact">
              <Button 
                variant="hero" 
                size="xl" 
                className="w-full sm:w-auto text-lg lg:text-xl px-8 lg:px-12 py-4 lg:py-6 min-h-[56px] lg:min-h-[64px] animate-glow-pulse shadow-2xl font-black tracking-wide animate-bounce-in group"
                style={{animationDelay: '1s'}}
              >
                <span className="relative z-10">🚀 Build My Content Empire</span>
              </Button>
            </Link>
            <Button 
              variant="glass" 
              size="xl" 
              className="w-full sm:w-auto text-lg lg:text-xl px-8 lg:px-12 py-4 lg:py-6 min-h-[56px] lg:min-h-[64px] font-bold animate-bounce-in"
              style={{animationDelay: '1.1s'}}
              onClick={handleScrollToHowItWorks}
            >
              <span className="relative z-10">📋 See How it Works</span>
            </Button>
          </div>

          {/* Enhanced Social Proof - Optimized */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 lg:p-6 max-w-3xl mx-auto animate-slide-up mb-10 lg:mb-16" style={{animationDelay: '1.2s'}}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 lg:gap-8">
              <div className="flex items-center gap-3 text-sm lg:text-base font-medium text-foreground">
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                </div>
                <span>No ongoing retainers</span>
              </div>
              
              <div className="flex items-center gap-3 text-sm lg:text-base font-medium text-foreground">
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                </div>
                <span>100% ownership</span>
              </div>
              
              <div className="flex items-center gap-3 text-sm lg:text-base font-medium text-foreground">
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20,6 9,17 4,12" />
                  </svg>
                </div>
                <span>100% Team Loyalty</span>
              </div>
            </div>
          </div>

          {/* Enhanced Proof Points */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 px-4 sm:px-0">
            <div className="glass-panel p-4 lg:p-6 text-center group animate-bounce-in" style={{animationDelay: '1.3s'}}>
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-primary font-bold text-xl">✓</span>
              </div>
              <p className="text-sm lg:text-base font-semibold text-foreground">Your Team Your Assets</p>
            </div>
            <div className="glass-panel p-4 lg:p-6 text-center group animate-bounce-in" style={{animationDelay: '1.4s'}}>
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-accent font-bold text-xl">⚡</span>
              </div>
              <p className="text-sm lg:text-base font-semibold text-foreground">Battle-Tested Systems</p>
            </div>
            <div className="glass-panel p-4 lg:p-6 text-center group animate-bounce-in" style={{animationDelay: '1.5s'}}>
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-secondary font-bold text-xl">💰</span>
              </div>
              <p className="text-sm lg:text-base font-semibold text-foreground">3-12x ROI vs Agencies</p>
            </div>
            <div className="glass-panel p-4 lg:p-6 text-center group animate-bounce-in" style={{animationDelay: '1.6s'}}>
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-primary font-bold text-xl">🎯</span>
              </div>
              <p className="text-sm lg:text-base font-semibold text-foreground">Results Driven, Not Vanity</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

OptimizedHero.displayName = 'OptimizedHero';

export default OptimizedHero;